/**
 * ModalityScreen
 *
 * Asks which treatment path the user is considering.
 * Only shown for the "pregnancy" goal.
 * Affects which journey definition is used.
 */

import React, { useRef } from "react";
import { View, Text, Pressable, ScrollView, Animated } from "react-native";
import { Syringe, Heart, HelpCircle, Snowflake } from "lucide-react-native";
import { EMBER } from "@louise/moon-ui/theme/tokens";
import WarmButton from "@louise/moon-ui/primitives/WarmButton";
import { useTranslations } from "@src/packages/i18n";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import { usePlanBuilder } from "../PlanBuilderContext";
import type { TreatmentModality } from "../types";

type Props = {
  onNext: () => void;
};

const OPTIONS: {
  id: TreatmentModality;
  labelKey: string;
  descriptionKey: string;
  icon: typeof Syringe;
}[] = [
  {
    id: "ivf",
    labelKey: "plan_builder.modality.ivf_label",
    descriptionKey: "plan_builder.modality.ivf_description",
    icon: Syringe,
  },
  {
    id: "iui",
    labelKey: "plan_builder.modality.iui_label",
    descriptionKey: "plan_builder.modality.iui_description",
    icon: Heart,
  },
  {
    id: "fet",
    labelKey: "plan_builder.modality.fet_label",
    descriptionKey: "plan_builder.modality.fet_description",
    icon: Snowflake,
  },
  {
    id: "not-sure",
    labelKey: "plan_builder.modality.not_sure_label",
    descriptionKey: "plan_builder.modality.not_sure_description",
    icon: HelpCircle,
  },
];

function OptionButton({
  label,
  description,
  Icon,
  isSelected,
  onPress,
}: {
  label: string;
  description: string;
  Icon: typeof Syringe;
  isSelected: boolean;
  onPress: () => void;
}) {
  const scale = useRef(new Animated.Value(1)).current;

  const handlePressIn = () => {
    Animated.spring(scale, {
      toValue: 0.97,
      useNativeDriver: true,
    }).start();
  };

  const handlePressOut = () => {
    Animated.spring(scale, {
      toValue: 1,
      friction: 4,
      useNativeDriver: true,
    }).start();
  };

  return (
    <Pressable
      onPress={onPress}
      onPressIn={handlePressIn}
      onPressOut={handlePressOut}
      accessibilityRole="button"
      accessibilityState={{ selected: isSelected }}
      // Inert testability hook: react-native-web 0.21 no longer forwards
      // accessibilityState.selected to the DOM, so mirror it via the
      // aria-selected prop for deterministic E2E assertions.
      aria-selected={isSelected}
    >
      <Animated.View
        style={{
          transform: [{ scale }],
          flexDirection: "row",
          alignItems: "center",
          paddingHorizontal: 18,
          paddingVertical: 16,
          backgroundColor: isSelected
            ? `${EMBER.colors.text}10`
            : "rgba(255, 255, 255, 0.82)",
          borderRadius: 14,
          borderWidth: 1,
          borderColor: isSelected
            ? EMBER.colors.text
            : "rgba(255, 255, 255, 0.9)",
          shadowColor: "#000",
          shadowOffset: { width: 0, height: 1 },
          shadowOpacity: 0.04,
          shadowRadius: 8,
          gap: 14,
        }}
      >
        <Icon
          size={22}
          color={isSelected ? EMBER.colors.text : EMBER.colors.textLight}
        />
        <View style={{ flex: 1 }}>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 15,
              fontWeight: isSelected ? "600" : "500",
              color: EMBER.colors.text,
              marginBottom: 2,
            }}
          >
            {label}
          </Text>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 13,
              fontWeight: "400",
              color: EMBER.colors.textLight,
              lineHeight: 18,
            }}
          >
            {description}
          </Text>
        </View>
        {/* Checkbox indicator */}
        <View
          style={{
            width: 22,
            height: 22,
            borderRadius: 6,
            borderWidth: 2,
            borderColor: isSelected ? EMBER.colors.text : EMBER.colors.border,
            backgroundColor: isSelected ? EMBER.colors.text : "transparent",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {isSelected && (
            <Text
              style={{
                color: "#fff",
                fontSize: 13,
                fontWeight: "700",
                lineHeight: 15,
              }}
            >
              ✓
            </Text>
          )}
        </View>
      </Animated.View>
    </Pressable>
  );
}

export default function ModalityScreen({ onNext }: Props) {
  const t = useTranslations();
  const capturePress = usePressCapture("ModalityScreen");
  const { state, updateState } = usePlanBuilder();

  const handleToggle = (id: TreatmentModality) => {
    if (id === "not-sure") {
      // "Not sure" is exclusive — clears other selections
      updateState({ modalities: ["not-sure"] });
      return;
    }
    // Selecting a real treatment clears "not sure" and toggles the item
    const without = state.modalities.filter(
      (m) => m !== "not-sure" && m !== id,
    );
    const wasSelected = state.modalities.includes(id);
    updateState({ modalities: wasSelected ? without : [...without, id] });
  };

  return (
    <ScrollView
      contentContainerStyle={{ padding: 24, paddingBottom: 40 }}
      showsVerticalScrollIndicator={false}
    >
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 22,
          fontWeight: "700",
          color: EMBER.colors.text,
          lineHeight: 28,
          marginBottom: 8,
        }}
      >
        {t("plan_builder.modality.title")}
      </Text>
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 15,
          fontWeight: "400",
          color: EMBER.colors.textLight,
          lineHeight: 22,
          marginBottom: 24,
        }}
      >
        {t("plan_builder.modality.subtitle")}
      </Text>

      <View style={{ gap: 8 }}>
        {OPTIONS.map((opt) => (
          <OptionButton
            key={opt.id}
            label={t(opt.labelKey)}
            description={t(opt.descriptionKey)}
            Icon={opt.icon}
            isSelected={state.modalities.includes(opt.id)}
            onPress={() => {
              capturePress(pressLabel(t(opt.labelKey)), opt.id);
              handleToggle(opt.id);
            }}
          />
        ))}
      </View>

      {state.modalities.length > 0 && (
        <View style={{ marginTop: 24 }}>
          <WarmButton title={t("plan_builder.continue")} onPress={onNext} />
        </View>
      )}
    </ScrollView>
  );
}
